/**
 * Fetching and opening a document PDF (spec §7, §9, §11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO CUSTOM PDF VIEWER IN THIS APP, AND THERE MUST NOT BE.
 *
 * On a phone, "preview the bill" means "open it" — Android hands the file to
 * the viewer the owner already knows, with the zoom and share controls he
 * already uses. A `pdf.js` canvas would add ~300 KB to a bundle loaded over
 * workshop mobile data (§1.1), reimplement pinch-zoom worse than the platform,
 * and break the one gesture (long-press → share) he actually performs. On the
 * office desktop the browser's built-in viewer does the same job inside an
 * `<object>`.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHY THE BYTES ARE FETCHED RATHER THAN LINKED. The PDF routes authenticate
 * with a bearer token (§1.1), and a browser navigation to `/invoices/7/pdf`
 * sends no Authorization header — it would 401 and, worse, the 401 would look
 * to `lib/api.ts` like an expired session. So the bytes come through the API
 * client and become an object URL. That object URL is same-origin, revocable,
 * and dies with the tab.
 */

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * `DocumentKind`, `fallbackPdfFilename` AND `fetchDocumentPdf` WERE DELETED HERE
 * ON 2026-08-16, AND DELETING THEM WAS THE FIX.
 *
 * They were a two-armed helper — `type DocumentKind = "invoice" | "quotation"`,
 * a ternary yielding `"Bill"`/`"Quotation"` for the filename, and a second
 * ternary picking the endpoint — with ZERO callers anywhere in the app. Every
 * screen calls `requestBlob` with its own endpoint directly, and all three
 * document types have done so since §17.7 shipped.
 *
 * Dead code shaped like a trap is worse than dead code. Both ternaries resolve
 * a challan to the QUOTATION arm, so the first person to reach for this
 * plausible-looking helper would have fetched the wrong document's bytes and
 * saved them as "Quotation CHL/26-27/7.pdf" — a wrong file with a confident
 * name. Widening it was the other option and it would have been maintaining a
 * third copy of a mapping nothing consults.
 *
 * WHAT SURVIVES BELOW is the part that has callers and is kind-agnostic: object
 * URLs and their revocation, and the two ways of putting a blob in front of
 * someone. None of it needs to know what document it is holding.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* -------------------------------------------------------------------------- */
/* Object URLs                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * An object URL, and the one function that must be called to release it.
 *
 * Object URLs are held by the document until revoked — a screen that creates
 * one per preview and never revokes leaks the whole PDF into memory each time,
 * which on a phone with several bills open is how the tab gets killed.
 */
export function createObjectUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

export function revokeObjectUrl(url: string | null): void {
  if (url !== null) URL.revokeObjectURL(url);
}

/**
 * Save the document to the device.
 *
 * A synthetic anchor with `download`, not `window.open`: `open` on Android
 * hands the blob to a viewer with no name attached, and the owner ends up with
 * a folder of `blob` files he cannot tell apart. The `download` attribute is
 * what puts `Bill 26-27-67 Lupin Limited.pdf` on the device.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined") return;
  const url = createObjectUrl(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously can beat the browser to
  // reading the href, and the download silently produces a 0-byte file.
  window.setTimeout(() => {
    revokeObjectUrl(url);
  }, 0);
}

/**
 * Open the document in whatever the platform uses to read PDFs.
 *
 * Returns the object URL so the caller can revoke it when its screen unmounts;
 * it CANNOT be revoked here, because the new tab is still reading from it.
 */
export function openBlobInNewTab(blob: Blob): string | null {
  if (typeof window === "undefined") return null;
  const url = createObjectUrl(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (opened === null) {
    // Popup blocked — the owner tapped a button, so this is not a popup he did
    // not ask for. Navigating the current tab still shows him the bill.
    window.location.assign(url);
  }
  return url;
}
